import { z } from 'zod';
import { type BusinessKind, BUSINESS_KINDS, IMPORT_CONSENT_BASES, LEAD_SEGMENTS, type LeadSegment, type NextAction } from '../constants';
import { LEAD_COLUMNS, LEAD_SORT_FIELDS, LeadFilter, LeadSelection, type LeadColumn, type LeadSortField } from './leads';

const csvList = <T extends z.ZodTypeAny>(item: T) =>
  z.preprocess((v) => (typeof v === 'string' ? (v === '' ? [] : v.split(',')) : v), z.array(item));

/** One row of the lead table (GET /api/leads). */
export type LeadRowDTO = {
  id: string;
  name: string;
  kind: BusinessKind;
  segment: LeadSegment;
  city: string | null;
  region: string | null;
  country: string;
  score: number;
  tierKey: string;
  stageId: string;
  relationshipId: string;
  primaryContact: { id: string; name: string; title: string | null; email: string | null; phone: string | null } | null;
  phone: string | null;
  email: string | null;
  lastContactedAt: string | null;
  nextFollowUpAt: string | null;
  nextAction: NextAction | null;
  nextActionReason: string | null;
  /** Sum of open opportunities' estimated value; the business estimate when there is no open opportunity. */
  opportunityValue: number | null;
  currency: string;
  ownerId: string | null;
  sourceId: string | null;
  tagIds: string[];
  createdAt: string;
  doNotContact: boolean;
  deletedAt: string | null;
};

export type LeadListResponse = { items: LeadRowDTO[]; total: number; page: number; pageSize: number };

/** Human column headers (lead table + CSV export). */
export const LEAD_COLUMN_LABELS: Record<LeadColumn, string> = {
  business: 'Business',
  kind: 'Type',
  city: 'City',
  score: 'Score',
  tier: 'Tier',
  relationship: 'Relationship',
  stage: 'Stage',
  contact: 'Contact',
  phone: 'Phone',
  email: 'Email',
  lastContactedAt: 'Last contact',
  nextFollowUpAt: 'Next follow-up',
  nextAction: 'Next action',
  estimatedValue: 'Opportunity value',
  owner: 'Owner',
  source: 'Source',
  tags: 'Tags',
  createdAt: 'Created',
};

/** Maximum rows in one CSV export. */
export const LEAD_EXPORT_MAX_ROWS = 100_000;

const exportShape = {
  columns: csvList(z.enum(LEAD_COLUMNS)).optional(),
  sort: z.enum(LEAD_SORT_FIELDS).default('score'),
  dir: z.enum(['asc', 'desc']).default('desc'),
};

/**
 * GET /api/leads/export — the list filter + columns. `ids` exports an explicit selection (short lists;
 * use POST /api/leads/export with a LeadSelection for large or filter-mode selections).
 */
export const LeadExportQuery = LeadFilter.extend({ ...exportShape, ids: csvList(z.uuid()).pipe(z.array(z.uuid()).max(300)).optional() });
export type LeadExportQuery = z.infer<typeof LeadExportQuery>;

/** POST /api/leads/export — export a bulk selection (same model as bulk actions). */
export const LeadExportBody = z.object({ selection: LeadSelection, ...exportShape });
export type LeadExportBody = z.infer<typeof LeadExportBody>;

export const LeadBulkAction = z.discriminatedUnion('type', [
  z.object({ type: z.literal('assign'), ownerId: z.uuid().nullable() }),
  z.object({ type: z.literal('stage'), stageKey: z.string().min(1).max(60) }),
  z.object({ type: z.literal('relationship'), relationshipKey: z.string().min(1).max(60) }),
  z.object({ type: z.literal('add_tags'), tagIds: z.array(z.uuid()).min(1).max(50) }),
  z.object({ type: z.literal('remove_tags'), tagIds: z.array(z.uuid()).min(1).max(50) }),
  z.object({ type: z.literal('delete') }),
  /** Record email consent on the leads' contacts that have none yet (never overrides a recorded or withdrawn one). */
  z.object({ type: z.literal('set_consent'), consentBasis: z.enum(IMPORT_CONSENT_BASES).exclude(['none']) }),
  /**
   * Read each lead's website for its published email in the background (leads without a website are skipped).
   * Found addresses are added as for POST /api/leads/:id/website-email and noted on the lead's timeline.
   */
  z.object({ type: z.literal('find_website_email') }),
  /** Move the leads to another list (pizzeria, dealer or rep) — for businesses imported or entered as the wrong kind. */
  z.object({ type: z.literal('set_kind'), kind: z.enum(BUSINESS_KINDS) }),
  /** Move pizzerias between the Leads, Prospects, Clients and Test Kitchens lists. */
  z.object({ type: z.literal('set_segment'), segment: z.enum(LEAD_SEGMENTS) }),
]);
export type LeadBulkAction = z.infer<typeof LeadBulkAction>;
export type LeadBulkActionType = LeadBulkAction['type'];

/** POST /api/leads/bulk-update */
export const LeadBulkUpdateBody = z.object({ selection: LeadSelection, action: LeadBulkAction });
export type LeadBulkUpdateBody = z.infer<typeof LeadBulkUpdateBody>;

export type LeadBulkUpdateResult = {
  /** Leads the action was applied to (including ones already in the requested state). */
  updated: number;
  /** Leads left untouched and why (e.g. assigned to another salesperson). */
  skipped: { id: string; name: string; reason: string }[];
  /** set_consent only — contacts given the consent, contacts added for a business email, contacts left as they were. */
  consent?: { updated: number; created: number; skipped: number };
  /** find_website_email only — leads queued and distinct websites to read (branches of a chain share one). */
  websiteLookups?: { queued: number; sites: number };
};

/** Saved lead-table views. Soft-deleted leads (`deleted`) cannot be part of a saved view. */
export const SavedViewFilters = LeadFilter.omit({ deleted: true });
export type SavedViewFilters = z.infer<typeof SavedViewFilters>;

export const CreateSavedViewBody = z.object({
  name: z.string().trim().min(1, 'Required').max(80),
  filters: SavedViewFilters.default({}),
  sortBy: z.enum(LEAD_SORT_FIELDS).nullable().optional(),
  sortDir: z.enum(['asc', 'desc']).nullable().optional(),
  columns: z.array(z.enum(LEAD_COLUMNS)).min(1).max(LEAD_COLUMNS.length).nullable().optional(),
  /** Visible to the whole team (managers and admins only). */
  isShared: z.boolean().optional(),
});
export type CreateSavedViewBody = z.infer<typeof CreateSavedViewBody>;

export const UpdateSavedViewBody = CreateSavedViewBody.partial();
export type UpdateSavedViewBody = z.infer<typeof UpdateSavedViewBody>;

export type SavedViewDTO = {
  id: string;
  name: string;
  filters: SavedViewFilters;
  sortBy: LeadSortField | null;
  sortDir: 'asc' | 'desc' | null;
  columns: LeadColumn[] | null;
  isShared: boolean;
  isSystem: boolean;
  ownerId: string | null;
  /** Whether the current user may rename/update/delete it. */
  canEdit: boolean;
};
